/**
 * About — full mission page for Benevita Home Health Care Ltd.
 *
 * Sections:
 *  1. Mission / story  — about.body + stats (driven by config.about)
 *  2. Core values      — driven by config.values
 *  3. Founding team    — driven by config.team
 *  4. CTA band
 */
import React from 'react';
import MarketingLayout from './MarketingLayout';
import Section from './components/Section';
import CTABand from './components/CTABand';
import FeatureCard from './components/FeatureCard';
import useSiteConfig from './useSiteConfig';
import { COLOR, FONT, RADIUS } from '../../components/ss/tokens';

/* -------------------------------------------------------------------------- */
/* Founding team card                                                           */
/* -------------------------------------------------------------------------- */
function TeamCard({ name, role, initials, bio }) {
  return (
    <div
      style={{
        background: '#fff',
        border: `1px solid ${COLOR.grayBorder}`,
        borderRadius: RADIUS.lg,
        padding: 24,
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
        <div
          style={{
            width: 52,
            height: 52,
            borderRadius: '50%',
            background: COLOR.blue,
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 18,
            fontWeight: FONT.weight.bold,
            flexShrink: 0,
          }}
        >
          {initials || (name || '').split(' ').map(w => w[0]).join('').slice(0, 2)}
        </div>
        <div>
          <div
            style={{
              fontSize: 17,
              fontWeight: FONT.weight.bold,
              color: COLOR.text,
              lineHeight: 1.3,
            }}
          >
            {name}
          </div>
          <div
            style={{
              fontSize: FONT.size.sm,
              fontWeight: FONT.weight.semi,
              color: COLOR.blue,
              marginTop: 2,
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
            }}
          >
            {role}
          </div>
        </div>
      </div>
      <p
        style={{
          margin: 0,
          fontSize: FONT.size.md,
          lineHeight: 1.65,
          color: COLOR.textMuted,
        }}
      >
        {bio}
      </p>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Vision / Mission panel                                                       */
/* -------------------------------------------------------------------------- */
function VisionMissionPanel({ vision, mission }) {
  if (!vision && !mission) return null;
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 20,
        marginTop: 48,
        maxWidth: 900,
        marginLeft: 'auto',
        marginRight: 'auto',
      }}
      className="hhms-vm-grid"
    >
      {vision && (
        <div
          style={{
            background: COLOR.blueBg,
            border: `1px solid ${COLOR.blueBorder}`,
            borderRadius: RADIUS.lg,
            padding: 24,
          }}
        >
          <div
            style={{
              fontSize: FONT.size.sm,
              fontWeight: FONT.weight.bold,
              color: COLOR.blue,
              textTransform: 'uppercase',
              letterSpacing: '1.4px',
              marginBottom: 10,
            }}
          >
            Our vision
          </div>
          <p
            style={{
              margin: 0,
              fontSize: FONT.size.md,
              lineHeight: 1.7,
              color: COLOR.textSub,
            }}
          >
            {vision}
          </p>
        </div>
      )}
      {mission && (
        <div
          style={{
            background: COLOR.grayBg,
            border: `1px solid ${COLOR.grayBorder}`,
            borderRadius: RADIUS.lg,
            padding: 24,
          }}
        >
          <div
            style={{
              fontSize: FONT.size.sm,
              fontWeight: FONT.weight.bold,
              color: COLOR.blue,
              textTransform: 'uppercase',
              letterSpacing: '1.4px',
              marginBottom: 10,
            }}
          >
            Our mission
          </div>
          <p
            style={{
              margin: 0,
              fontSize: FONT.size.md,
              lineHeight: 1.7,
              color: COLOR.textSub,
            }}
          >
            {mission}
          </p>
        </div>
      )}
      <style>{`
        @media (max-width: 640px) {
          .hhms-vm-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Main export                                                                  */
/* -------------------------------------------------------------------------- */
export default function About() {
  const { config } = useSiteConfig();
  const about  = config.about  || {};
  const values = config.values || {};
  const team   = config.team   || {};
  const fb     = config.ui?.fallbacks || {};

  return (
    <MarketingLayout>

      {/* ── 1. Story / Mission / Vision ─────────────────────────────────── */}
      <Section
        background="#fff"
        paddingY={88}
        eyebrow={about.eyebrow || fb.aboutEyebrow}
        title={about.title || fb.aboutTitle}
        align="center"
      >
        <div
          style={{
            maxWidth: 760,
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 22,
          }}
        >
          {(about.body || []).map((p, i) => (
            <p
              key={i}
              style={{
                margin: 0,
                fontSize: 17,
                lineHeight: 1.75,
                color: COLOR.textSub,
              }}
            >
              {p}
            </p>
          ))}
        </div>

        {/* Vision & Mission panels */}
        <VisionMissionPanel vision={about.vision} mission={about.mission} />

        {/* Stats strip */}
        {about.stats?.length > 0 && (
          <div
            style={{
              marginTop: 56,
              display: 'grid',
              gridTemplateColumns: `repeat(${Math.min(about.stats.length, 4)}, 1fr)`,
              gap: 20,
              maxWidth: 1180,
              marginLeft: 'auto',
              marginRight: 'auto',
            }}
            className="hhms-about-stats"
          >
            {about.stats.map((s, i) => (
              <div
                key={i}
                style={{
                  background: COLOR.blueBg,
                  border: `1px solid ${COLOR.blueBorder}`,
                  borderRadius: RADIUS.lg,
                  padding: 22,
                  textAlign: 'center',
                }}
              >
                <div
                  style={{
                    fontSize: 28,
                    fontWeight: FONT.weight.xbold,
                    color: COLOR.blue,
                    letterSpacing: '-0.5px',
                  }}
                >
                  {s.value}
                </div>
                <div style={{ marginTop: 4, fontSize: FONT.size.sm, color: COLOR.textSub }}>
                  {s.label}
                </div>
              </div>
            ))}
          </div>
        )}

        <style>{`
          @media (max-width: 720px) {
            .hhms-about-stats { grid-template-columns: 1fr 1fr !important; }
          }
        `}</style>
      </Section>

      {/* ── 2. Core values ──────────────────────────────────────────────── */}
      {values.items?.length > 0 && (
        <Section
          id="values"
          background={COLOR.grayBg}
          eyebrow={values.eyebrow}
          title={values.title}
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: 20,
            }}
          >
            {values.items.map((v, i) => (
              <FeatureCard key={i} icon={v.icon} title={v.title} body={v.body} />
            ))}
          </div>
        </Section>
      )}

      {/* ── 3. Founding team ────────────────────────────────────────────── */}
      {team.members?.length > 0 && (
        <Section
          id="team"
          background="#fff"
          eyebrow={team.eyebrow}
          title={team.title}
          subtitle={team.subtitle}
          align="center"
        >
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 20,
              maxWidth: 1200,
              margin: '0 auto',
            }}
          >
            {team.members.map((m, i) => (
              <TeamCard
                key={i}
                name={m.name}
                role={m.role}
                initials={m.initials}
                bio={m.bio}
              />
            ))}
          </div>
        </Section>
      )}

      {/* ── 4. CTA ──────────────────────────────────────────────────────── */}
      <CTABand />

    </MarketingLayout>
  );
}
